import type {
  BlockType,
  CourseBlock,
  LessonBlockTranslationFields,
} from '@course-authoring/shared-types';

export interface BlockTranslationFieldDefinition {
  key: string;
  label: string;
  multiline?: boolean;
  kind: 'plainText' | 'richText';
}

interface StableChildrenDefinition {
  collectionKey: 'items' | 'topics';
  fields: readonly BlockTranslationFieldDefinition[];
}

interface FixedOptionsDefinition {
  optionKeys: readonly ['option1', 'option2'];
  fields: readonly BlockTranslationFieldDefinition[];
}

export interface BlockTranslationFieldDescriptor {
  fields: readonly BlockTranslationFieldDefinition[];
  children?: StableChildrenDefinition;
  options?: FixedOptionsDefinition;
}

const field = (
  key: string,
  label: string,
  multiline = false,
  kind: BlockTranslationFieldDefinition['kind'] = 'plainText',
): BlockTranslationFieldDefinition => ({ key, label, kind, ...(multiline ? { multiline } : {}) });

const richTextField = (
  key: string,
  label: string,
  multiline = false,
): BlockTranslationFieldDefinition => field(key, label, multiline, 'richText');

const DESCRIPTION_FIELDS = [field('title', 'Title'), richTextField('description', 'Description', true)] as const;
const TITLE_ONLY_FIELDS = [field('title', 'Title')] as const;
const COMPARISON_OPTION_FIELDS = [
  richTextField('title', 'Title'),
  richTextField('body', 'Body', true),
  field('videoTitle', 'Video Title'),
  field('videoShortDescription', 'Video Short Description', true),
  field('collapsedLabel', 'Collapsed Label'),
] as const;
const INTERACTION_MESSAGE_FIELD = field('interactionMessage', 'Informational Message');

export const BLOCK_TRANSLATION_FIELD_DESCRIPTORS = {
  video: {
    fields: [
      field('title', 'Title'),
      field('shortDescription', 'Short description', true),
      richTextField('longDescription', 'Long description', true),
      INTERACTION_MESSAGE_FIELD,
    ],
  },
  'basic-content-a': {
    fields: [field('title', 'Title'), richTextField('body', 'Body', true), field('imageAlt', 'Image alt text', true)],
  },
  'basic-content-b': {
    fields: [field('title', 'Title'), richTextField('body', 'Body', true), field('imageAlt', 'Image alt text', true)],
  },
  'stylized-text-callout': {
    fields: [richTextField('content', 'Content', true)],
  },
  'stylized-numbered-list': {
    fields: TITLE_ONLY_FIELDS,
    children: {
      collectionKey: 'items',
      fields: [richTextField('content', 'Item content', true)],
    },
  },
  'stylized-sidebar-callout': {
    fields: [
      richTextField('title', 'Title'),
      richTextField('body', 'Body', true),
      richTextField('calloutTitle', 'Callout title'),
      richTextField('calloutBody', 'Callout body', true),
    ],
  },
  'comparison-slider': {
    fields: [
      richTextField('title', 'Title'),
      richTextField('subtitle', 'Subtitle', true),
      INTERACTION_MESSAGE_FIELD,
    ],
    options: {
      optionKeys: ['option1', 'option2'],
      fields: COMPARISON_OPTION_FIELDS,
    },
  },
  'click-expand': {
    fields: [...DESCRIPTION_FIELDS, INTERACTION_MESSAGE_FIELD],
    children: {
      collectionKey: 'topics',
      fields: [field('title', 'Topic title'), richTextField('content', 'Topic content', true)],
    },
  },
  'policy-acknowledgement': { fields: [...DESCRIPTION_FIELDS, INTERACTION_MESSAGE_FIELD] },
  'electronic-signature': {
    fields: [
      ...DESCRIPTION_FIELDS,
      field('checkboxLabel', 'Checkbox label', true),
      INTERACTION_MESSAGE_FIELD,
    ],
  },
  map: { fields: DESCRIPTION_FIELDS },
  // NA-384: title moved off these four's unconditional fields onto
  // OVERRIDABLE_FIELDS_BY_TYPE below — it is translatable only while a block
  // has taken ownership of it from the Question Bank, same as question text.
  'likert-scale': { fields: [] },
  'single-choice-answer': { fields: [] },
  'workplace-color-spectrum': { fields: [] },
  'freeform-text': { fields: [] },
  categorization: {
    fields: [
      field('correctFeedbackHeading', 'Correct heading'),
      richTextField('correctFeedbackBody', 'Correct feedback', true),
      field('incorrectFeedbackHeading', 'Incorrect heading'),
      richTextField('incorrectFeedbackBody', 'Incorrect feedback', true),
    ],
  },
  'lesson-complete': { fields: DESCRIPTION_FIELDS },
  link: { fields: [...DESCRIPTION_FIELDS, field('linkText', 'Link Text'), INTERACTION_MESSAGE_FIELD] },
} satisfies Partial<Record<BlockType, BlockTranslationFieldDescriptor>>;

/**
 * NA-313 / NA-382: some Question Bank-owned fields are translatable per BLOCK
 * INSTANCE, not per block type — only for a block that has taken ownership of
 * them. Every other field's translatability is a property of the type, which is
 * why the map above can be static.
 *
 * Mirrors author-api `lessonBlockTranslationFields.ts`. The two repos cannot
 * import from each other, so keep them in step: the server refuses a translation
 * write to these fields unless the same condition holds.
 */
interface OverridableTranslationField {
  field: BlockTranslationFieldDefinition;
  marker: string;
  /** Only gated Single Choice has a correct-answer explanation at all. */
  requiresGated?: boolean;
}

// NA-382: every bank-backed type names its question text `description`.
const QUESTION_TEXT_TRANSLATION: OverridableTranslationField = {
  field: field('description', 'Question text', true, 'richText'),
  marker: 'descriptionOverridden',
};

// NA-384: every bank-backed type names its title `title` — same shared-name
// shape as question text, unconditional on gatedness. Plain text, unlike
// every other overridable field here.
const TITLE_TRANSLATION: OverridableTranslationField = {
  field: field('title', 'Title'),
  marker: 'titleOverridden',
};

const OVERRIDABLE_FIELDS_BY_TYPE: Partial<Record<BlockType, readonly OverridableTranslationField[]>> = {
  'single-choice-answer': [
    QUESTION_TEXT_TRANSLATION,
    TITLE_TRANSLATION,
    {
      field: field('correctExplanation', 'Correct answer explanation', true),
      marker: 'correctExplanationOverridden',
      requiresGated: true,
    },
  ],
  'workplace-color-spectrum': [
    QUESTION_TEXT_TRANSLATION,
    TITLE_TRANSLATION,
    {
      field: field('correctAnswerExplanation', 'Correct answer explanation', true),
      marker: 'correctAnswerExplanationOverridden',
    },
  ],
  'likert-scale': [QUESTION_TEXT_TRANSLATION, TITLE_TRANSLATION],
  categorization: [QUESTION_TEXT_TRANSLATION, TITLE_TRANSLATION],
  'freeform-text': [QUESTION_TEXT_TRANSLATION, TITLE_TRANSLATION],
};

/**
 * The descriptor for a block.
 *
 * Pass `sourceBlock` — the default-locale block — wherever it is available, so a
 * field the block owns becomes translatable. Ownership is read from that source
 * scope only, never from a localized row, so a translation can neither claim nor
 * release the field. Called with just a type, the static per-type answer is used.
 */
export function getBlockTranslationFieldDescriptor(
  type: BlockType,
  sourceBlock?: CourseBlock,
): BlockTranslationFieldDescriptor | undefined {
  const descriptor = BLOCK_TRANSLATION_FIELD_DESCRIPTORS[
    type as keyof typeof BLOCK_TRANSLATION_FIELD_DESCRIPTORS
  ] as BlockTranslationFieldDescriptor | undefined;
  if (!descriptor || !sourceBlock) return descriptor;

  const overridables = OVERRIDABLE_FIELDS_BY_TYPE[type];
  if (!overridables) return descriptor;

  const source = sourceBlock as unknown as Record<string, unknown>;
  const owned = overridables.filter((overridable) => {
    if (source[overridable.marker] !== true) return false;
    if (overridable.requiresGated && source.gated !== true) return false;
    return true;
  });
  if (owned.length === 0) return descriptor;

  return {
    ...descriptor,
    fields: [...descriptor.fields, ...owned.map((overridable) => overridable.field)],
  };
}

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const pickPresent = (
  value: Record<string, unknown>,
  definitions: readonly BlockTranslationFieldDefinition[],
) => Object.fromEntries(
  definitions
    .filter(({ key }) => hasOwn(value, key))
    .map(({ key }) => [key, value[key]]),
);

const pick = (value: Record<string, unknown>, definitions: readonly BlockTranslationFieldDefinition[]) =>
  Object.fromEntries(definitions.map(({ key }) => [key, value[key]]));

const canonicalEmptyValue = (definition: BlockTranslationFieldDefinition): unknown =>
  definition.kind === 'richText' ? { type: 'doc', content: [] } : '';

// Match AuthorAPI's top-level projection so legacy payloads with an absent
// optional field compare equal to the schema's empty value.
const pickCanonicalTopLevel = (
  value: Record<string, unknown>,
  definitions: readonly BlockTranslationFieldDefinition[],
) => Object.fromEntries(definitions.map((definition) => [
  definition.key,
  value[definition.key] === undefined
    ? canonicalEmptyValue(definition)
    : value[definition.key],
]));

const canonicalChildren = (
  value: unknown,
  definitions: readonly BlockTranslationFieldDefinition[],
) => (Array.isArray(value) ? value : [])
  .map(asObject)
  .map((child) => ({ id: child.id, ...pick(child, definitions) }))
  .sort((left, right) => String(left.id).localeCompare(String(right.id)));

export function canonicalTranslatableFields(block: CourseBlock): unknown {
  // NA-313 / NA-382: pass the block so an owned field counts as translatable —
  // this also makes taking ownership register as a source change for translation
  // status, which is why overriding question text flags existing translations.
  const descriptor = getBlockTranslationFieldDescriptor(block.type, block);
  if (!descriptor) return {};
  const value = block as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = pickCanonicalTopLevel(value, descriptor.fields);
  if (descriptor.children) {
    result[descriptor.children.collectionKey] = canonicalChildren(
      value[descriptor.children.collectionKey],
      descriptor.children.fields,
    );
  }
  if (descriptor.options) {
    for (const optionKey of descriptor.options.optionKeys) {
      result[optionKey] = pick(asObject(value[optionKey]), descriptor.options.fields);
    }
  }
  return result;
}

export function hasTranslatableUpdate(
  block: CourseBlock,
  updates: Partial<CourseBlock>,
): boolean {
  const next = { ...block, ...updates } as CourseBlock;
  return JSON.stringify(canonicalTranslatableFields(block))
    !== JSON.stringify(canonicalTranslatableFields(next));
}

export function mergeBlockTranslationFields(
  sourceBlock: CourseBlock,
  fields: LessonBlockTranslationFields,
): CourseBlock {
  const descriptor = getBlockTranslationFieldDescriptor(sourceBlock.type, sourceBlock);
  if (!descriptor) return sourceBlock;

  const source = sourceBlock as unknown as Record<string, unknown>;
  const merged: Record<string, unknown> = {
    ...source,
    ...pickPresent(fields, descriptor.fields),
  };

  if (descriptor.children) {
    const collectionKey = descriptor.children.collectionKey;
    const localizedById = new Map(
      (Array.isArray(fields[collectionKey]) ? fields[collectionKey] : [])
        .map(asObject)
        .map((child) => [String(child.id), child]),
    );
    merged[collectionKey] = (Array.isArray(source[collectionKey]) ? source[collectionKey] : [])
      .map(asObject)
      .map((child) => ({
        ...child,
        ...pickPresent(
          localizedById.get(String(child.id)) ?? {},
          descriptor.children?.fields ?? [],
        ),
      }));
  }

  if (descriptor.options) {
    for (const optionKey of descriptor.options.optionKeys) {
      merged[optionKey] = {
        ...asObject(source[optionKey]),
        ...pickPresent(asObject(fields[optionKey]), descriptor.options.fields),
      };
    }
  }

  return merged as unknown as CourseBlock;
}

export function filterBlockTranslationUpdates(
  type: BlockType,
  updates: Partial<CourseBlock>,
  sourceBlock?: CourseBlock,
): LessonBlockTranslationFields {
  // NA-313 / NA-382: `sourceBlock` is the default-locale block; without it an
  // owned explanation or question text would be filtered out of the write.
  const descriptor = getBlockTranslationFieldDescriptor(type, sourceBlock);
  if (!descriptor) return {};

  const value = updates as Record<string, unknown>;
  const filtered: LessonBlockTranslationFields = pickPresent(value, descriptor.fields);

  if (descriptor.children && hasOwn(value, descriptor.children.collectionKey)) {
    const collectionKey = descriptor.children.collectionKey;
    filtered[collectionKey] = (Array.isArray(value[collectionKey]) ? value[collectionKey] : [])
      .map(asObject)
      .map((child) => ({
        id: child.id,
        ...pickPresent(child, descriptor.children?.fields ?? []),
      }));
  }

  if (descriptor.options) {
    for (const optionKey of descriptor.options.optionKeys) {
      if (hasOwn(value, optionKey)) {
        filtered[optionKey] = pickPresent(
          asObject(value[optionKey]),
          descriptor.options.fields,
        );
      }
    }
  }

  return filtered;
}
